<script lang="ts" setup>
import { LModal } from '@wgg.hyve/hyve-ui-library'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import PurchaseDateModal from '../purchase-date/PurchaseDateModal.vue'
import type { ModalProps } from '@/ts/types/modal.types.ts'
import { omit } from '@/utils/data-utils.ts'
import YourTariffModal from '@/components/modal/content/your-tariff/YourTariffModal.vue'
import PriceMatrixModal from '@/components/modal/content/price-matrix/PriceMatrixModal.vue'
import PriceInfoModal from '@/components/modal/content/price-info/PriceInfoModal.vue'
import RedirectBlankModal from '@/components/modal/content/redirect-blank/RedirectBlankModal.vue'
import { useAppShadowRoot } from '@/composables/app-shadow-root/app-shadow-root.composable'
import { MODAL_CONTAINER_ID } from '@/ts/constants/constants'

export type InfoModalProps = {
  modalComponent: InfoModalComponent | null | undefined
  visible: boolean
} & ModalProps

export type InfoModalComponent = keyof typeof contentComponents

const props = withDefaults(defineProps<InfoModalProps>(), {
  visible: false
})

const modalRef = useTemplateRef<InstanceType<typeof LModal>>('modal')

const isVisible = computed(() => props.visible)

// Workaround to keep modal-specific payload away from LModal props to avoid Vue attribute warnings.
const lModalProps = computed(() => {
  return omit(props, ['modalComponent', 'visible', 'modalData'])
})

const modalContentProps = computed(() => {
  return omit(props, ['modalComponent', 'visible'])
})

const contentComponents = {
  PurchaseDateModal,
  PriceMatrixModal,
  PriceInfoModal,
  YourTariffModal,
  RedirectBlankModal
}

// workaround to keep html5 dialog syntax
watch(isVisible, (newValue) => {
  if (newValue) {
    modalRef.value?.show()
  } else {
    modalRef.value?.close()
  }
})
const modalTarget = ref<HTMLElement | null>(null)

onMounted(() => {
  // Get modal container element on mounted
  modalTarget.value = useAppShadowRoot()?.getElementById(MODAL_CONTAINER_ID) ?? null
})
</script>

<template>
  <!-- 
    @TODO: The Teleport:to prop used by LModal cannot find the modal container within the Site Connect shadow DOM by a string.
      But it accepts a HTMLElement as target. The UI-Lib should add it as allowed property.
    @TODO as soon as the ui lib supports HTMLElements as target for LModal remove the as unknown as string workaround
      It was necessary because you cannot ignore template type checks via es-lint-ignore-line
  -->
  <LModal :target="(modalTarget as unknown as string) ?? 'body'" ref="modal" v-bind="lModalProps">
    <template v-slot:content>
      <span v-if="!!lModalProps.content">{{ lModalProps.content }}</span>
      <div v-else class="info-modal__content">
        <component
          :is="contentComponents?.[modalComponent]"
          v-if="!!modalComponent"
          v-bind="modalContentProps"
        />
      </div>
    </template>
  </LModal>
</template>
